import { useTranslation } from "react-i18next";
import { getImage } from "../../../utils/Calculator";
import { CarPassengersIcon, CarSuitcaseIcon } from "../../icons";
import { isDiscountApplied } from "../../../utils/discount";

const PanasonicAutomotiveImageBackgroundWhite = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/panasonic-automotive-bg-white.png";

const VehicleTypeCard = ({
  vehicle_type,
  onClick,
  styleSelectedVehicle,
  disabledCard = false,
  isEditBooking = false,
  meetAssistPrice = 0,
  price,
  handleShowModal = () => { }
}) => {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === "jp";
  // Normal Meet & Assist is pre-selected at most airports and stations, so it is
  // part of what this vehicle actually costs. Shown folded into the price rather
  // than sprung on the customer at the confirmation step. It sits outside the
  // transfer discount, so it is added to both figures and the gap between them
  // stays equal to the discount.
  const displayPrice = (price || 0) + (meetAssistPrice || 0);
  const displayPriceBeforeDiscount = (vehicle_type.price || 0) + (meetAssistPrice || 0);
  const isWellCabinVehicle = vehicle_type?.typename === "Toyota Alphard WELL Cabin Luxe" || vehicle_type?.typename === "WELL Cabin GranLuxe";

  return (
    // No border here on purpose: the Bootstrap `.card` just below already draws
    // a 1px outline. A second border on this wrapper made the outline look
    // twice as thick as the cards on the Fleet Line-up page.
    <div className="tw:h-full tw:flex tw:flex-col tw:rounded-[5px] tw:relative tw:group tw:cursor-pointer">
      <div
        className={`card h-100 shadow-sm-2-hover gold-border-top-hover gold-footer-hover ${styleSelectedVehicle} pointer`}
        onClick={() => {
          if (window.innerWidth < 768) {
            handleShowModal()
            onClick(vehicle_type);
          }
          onClick(vehicle_type)
        }}
      >
        {
          vehicle_type.isPartnerVehicle && (
            <div className="header-vehicle-partner tw:py-1 text-center align-item-center">
              <p className="desc-partner-vehicle">
                {t("FleetPage.ProvidedByParter")}
              </p>
            </div>
          )
        }

        {/* Header with golden border */}
        <div className="tw:mt-[26px] tw:text-[8px] tw:text-[#282828] tw:text-center tw:tracking-[3px] tw:font-semibold">
          {t("FlatRate")}
        </div>

        {/* Price */}
        <div className="text-center py-2 tw:pt-[6px]">
          {
            isDiscountApplied(vehicle_type.discount) && (
              <h6 className="text-end before-discount-price tw:!mb-0">{t("JPY")} {displayPriceBeforeDiscount.toLocaleString()} {t("JPY.JP")}{" "}</h6>
            )
          }
          <div className="tw:font-libre-caslon-display tw:text-[22px] tw:md:text-[26px] tw:text-[#282828]">
            {t("JPY")}{" "}
            {displayPrice.toLocaleString()}{t("JPY.JP")}
          </div>
        </div>

        {/* Vehicle Image and Info Container */}
        <div className="card-body pb-1 tw:flex tw:items-center">
          <div className="row align-items-start pb-0 mb-0">
            {/* Vehicle Image */}
            <div className="col-12 col-lg-8">
              <div className="position-relative" style={{ height: "120px" }}>
                <img
                  src={vehicle_type.images.png || getImage(vehicle_type.typename)}
                  alt={vehicle_type.typename}
                  fill
                  className="card-img-top px-4 h-100"
                />
              </div>

              {/* Category and Name */}
              <div className="mb-3">
                <div className="card-header ps-1 pt-3 pb-2 tw:text-[#7C7B7B] tw:font-semibold tw:tracking-[3px]">
                  {t(vehicle_type.typename.replace(/ /g, ".") + ".Tag")}
                </div>
                <div className="card-body ps-1 pt-1 pb-0">
                  <h4 className="card-title mb-3">
                    <span className="d-lg-none d-sm-block tw:text-[#212529] tw:text-[22px] tw:font-libre-caslon-display">
                      {t(vehicle_type.typename.replace(/ /g, "."))}
                    </span>
                    <span className="d-none d-lg-block d-sm-none tw:text-[#212529] tw:text-[22px] tw:font-libre-caslon-display">
                      {vehicle_type?.typename?.startsWith("Mercedes Benz") ? (
                        <>
                          {t(`${vehicle_type.typename.split(" ")[0]}.${vehicle_type.typename.split(" ")[1]}`)} <br />
                          {t(`${vehicle_type.typename.split(" ")[2]}`)}
                        </>
                      ) : vehicle_type?.typename?.startsWith("Toyota HiAce") ? (
                        <>
                          {t(`${vehicle_type.typename.split(" ")[0]}.${vehicle_type.typename.split(" ")[1]}`)} <br />
                          {t(`${vehicle_type.typename.split(" ")[2]}.${vehicle_type.typename.split(" ")[3]}`)}
                        </>
                      ) : vehicle_type?.typename?.startsWith("Toyota Alphard WELL Cabin") ? (
                        <>
                          {t(`${vehicle_type.typename.split(" ")[0]}.${vehicle_type.typename.split(" ")[1]}`)} <br />
                          {t(`${vehicle_type.typename.split(" ")[2]}.${vehicle_type.typename.split(" ")[3]}.${vehicle_type.typename.split(" ")[4]}`)}
                        </>
                      ) : (
                        <>
                          {t(vehicle_type.typename.replace(/ /g, "."))}
                        </>
                      )}
                    </span>
                  </h4>
                </div>
              </div>
            </div>

            <hr className="d-block d-lg-none" style={{
              border: "none",
              height: "1px",
              backgroundColor: "#F4F4F4",
              width: "90%",
              margin: "1.5rem auto"
            }} />

            {/* Vehicle Info and Amenities */}
            <div className="col-12 col-lg-4">
              {/* Amenities - Only passengers and suitcases */}
              <div className="row mt-0 mt-lg-3">
                <div className="col-6 col-lg-12 align-items-start px-3 mb-3">
                  <div className="col-1 text-center align-item-center me-2 mb-2 mb-lg-1 tw:flex tw:items-center tw:justify-center" style={{ width: '32px', height: '32px' }}>
                    <CarPassengersIcon size={32} />
                  </div>
                  <div
                    className="tw:text-xs tw:md:text-sm tw:text-[#282828] "
                    dangerouslySetInnerHTML={{
                      __html: t("VehicleType.MaximumPassenger", {
                        maximumPax: vehicle_type.maxpassenger,
                      }),
                    }}
                  ></div>
                </div>

                <div className="col-6 col-lg-12 align-items-start px-3 mb-3 mb-lg-0">
                  <div className="col-1 text-center align-item-center me-2 mb-2 mb-lg-1 tw:flex tw:items-center tw:justify-center" style={{ width: '32px', height: '32px' }}>
                    <CarSuitcaseIcon size={17} />
                  </div>
                  <div
                    className="tw:text-xs tw:md:text-sm tw:text-[#282828] "
                    dangerouslySetInnerHTML={{
                      __html: t("VehicleType.MaximumLuggages", {
                        maximumLuggage: vehicle_type.maxluggages,
                      }),
                    }}
                  ></div>
                  <div className="tw:text-[#8D8D8D] tw:text-[12px] tw:!font-[400] tw:leading-4">
                    {t("FleetTypePage.suitcaseNote")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isWellCabinVehicle && (
          <div className="tw:bg-white tw:border-t tw:border-t-[#F1F1F1] tw:min-h-[32px] tw:px-4 tw:h-[50px] tw:flex tw:items-center tw:justify-start tw:gap-x-[10px]">
            <div className="tw:flex tw:items-center tw:gap-x-[8px]">
              {isJapanese ? (
                <>
                  <img
                    src={PanasonicAutomotiveImageBackgroundWhite}
                    alt="Panasonic Automotive"
                    className="tw:h-[18px] tw:w-auto tw:object-contain"
                  />
                  <span className="tw:text-[#282828] tw:text-[8px] tw:font-semibold tw:tracking-[3px] tw:uppercase tw:leading-none">
                    {t("FleetTypePage.ToyotaAlphardWELLCabin.CollaborationWith")}
                  </span>
                </>
              ) : (
                <>
                  <span className="tw:text-[#282828] tw:text-[8px] tw:font-semibold tw:tracking-[3px] tw:uppercase tw:leading-none">
                    {t("FleetTypePage.ToyotaAlphardWELLCabin.CollaborationWith")}
                  </span>
                  <img
                    src={PanasonicAutomotiveImageBackgroundWhite}
                    alt="Panasonic Automotive"
                    className="tw:h-[18px] tw:w-auto tw:object-contain"
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Book Now Button */}
        <div className={`card-footer border-top ${styleSelectedVehicle}-footer`}>
          <button
            onClick={() => {
              if (window.innerWidth < 768) {
                handleShowModal()
                onClick(vehicle_type);
              }
              onClick(vehicle_type)
            }}
            disabled={disabledCard}

            className={`btn w-100 py-2 tw:font-hanken-grotesk tw:text-sm tw:flex tw:items-center tw:justify-center`}
          >
            {styleSelectedVehicle == "selected-vehicle-card" ? t("Discover.Selected") : isEditBooking ? t("Discover.Select") : t("Discover.BookNow")}
            <i className="fa fa-arrow-right ms-2 arrow-right-book-icon"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleTypeCard;
